'use client';

import { ReactNode, useEffect, useState } from 'react';

import { getLastNavigationPath, useNavigate, usePathname, useQueryParam, useUrl } from '@/app/_hooks/useNavigate';
import { sendSentryBreadcrumb } from '@/app/_lib/sentry';
import { appRoutes } from '@/app/_routes';
import { AnalyticsEvents } from '@/lib/google/AnalyticsEvents';
import { analyticsCouriers } from '@/lib/google/analytics';

const PreventNavigateBack = (): ReactNode => {
  const url = useUrl();
  const { navigate } = useNavigate();
  const pathname = usePathname();
  const pnb = useQueryParam('pnb', false, 'boolean'); // prevent navigate back
  const [backNavigationCount, setBackNavigationCount] = useState(0);

  useEffect(() => {
    sendSentryBreadcrumb('Navigate', `url`, { url, backNavigationCount }, 'info');
  }, [url, backNavigationCount]);

  useEffect(() => {
    // Add pnb query param to the URL to prevent navigate back
    if (!pnb && haveToPreventGoBack(pathname)) {
      navigate(null, { keepPreviousQueryParams: true, queryParams: { pnb: true } });
    }
  }, [pathname, pnb]);

  useEffect(() => {
    window.addEventListener('popstate', () => {
      // Count the number of times the user clicks the back button
      setBackNavigationCount((prevBackNavigationCount) => prevBackNavigationCount + 1);
    });
  }, []);

  useEffect(() => {
    if (backNavigationCount > 0) {
      logBackNavigation(backNavigationCount, pathname);
      checkLastNavigationPath(pathname);
    }
  }, [backNavigationCount]);

  return null;
};

export default PreventNavigateBack;

const haveToPreventGoBack = (path: string): boolean => {
  const appRoute = Object.values(appRoutes).find((route) => route.path === path) as { preventGoBack: boolean };
  return appRoute?.preventGoBack ?? false;
};

const logBackNavigation = (backNavigationCount: number, pathname: string) => {
  // We want to know how many times you try to navigate backward where you shouldn't.
  if (haveToPreventGoBack(pathname)) {
    analyticsCouriers.sendEvent(AnalyticsEvents.BACK_NAVIGATION, { backNavigationCount });
  }
};

const checkLastNavigationPath = (pathname: string) => {
  // If the user clicks the back button repeatedly, the browser navigates to the previous paths.
  // We want to send a log if the user manages to bypass our back navigation prevention.
  setTimeout(() => {
    const lastNavigationPath = getLastNavigationPath();
    if (haveToPreventGoBack(pathname) && !lastNavigationPath.startsWith(pathname)) {
      analyticsCouriers.sendEvent(AnalyticsEvents.BACK_NAVIGATION_FORCED, { lastNavigationPath, pathname });
    }
  }, 1000);
};
